import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [busy, setBusy] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received - thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Please call us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEOHead title="Contact Our Synagogue in Melbourne FL" description="Contact Temple Israel of Brevard - the Reform synagogue serving Melbourne, FL and Brevard County. 7300 Lake Andrew Drive, Melbourne, FL 32940. Phone: (321) 631-9494." path="/contact" />
      <PageHero eyebrow="Get in Touch" title="We'd love to hear from you." subtitle="Questions about membership, lifecycle events, or just want to say shalom? Reach out - we usually respond within a business day." />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-5">
            <InfoCard icon={MapPin} title="Visit Us" testid="contact-info-visit">
              <p>7300 Lake Andrew Drive<br />Melbourne, FL 32940</p>
              <p className="mt-3 text-sm text-[var(--muted)]">Mailing: 2328 Citadel Way, Suite 103, Box 222, Melbourne, FL 32940</p>
            </InfoCard>
            <InfoCard icon={Phone} title="Call">
              <a href="tel:+13216319494" className="hover:text-[var(--blue-deep)] inline-block py-1.5">(321) 631-9494</a>
            </InfoCard>
            <InfoCard icon={Mail} title="Email">
              <a href="mailto:tioffice@tiofbrevard.com" className="break-all hover:text-[var(--blue-deep)] inline-block py-1.5">tioffice@tiofbrevard.com</a>
              <p className="mt-3 text-sm text-[var(--muted)]">Rabbi:{" "}
                <a href="mailto:rabbikarenfanwick@tiofbrevard.com" className="hover:text-[var(--blue-deep)] break-all inline-block py-1.5">rabbikarenfanwick@tiofbrevard.com</a>
              </p>
            </InfoCard>
            <InfoCard icon={Clock} title="Office Hours">
              <p>Tuesday - Friday<br />9:00 AM - 2:00 PM<br /><span className="text-sm text-[var(--muted)]">(By appointment only)</span></p>
              <p className="mt-3 text-sm"><strong className="text-[var(--blue-ink)]">Office Administrator:</strong> Terry Ginsburg</p>
            </InfoCard>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} className="card-soft p-7 md:p-10 space-y-5 bg-white" data-testid="contact-form">
              <h2 className="font-display text-3xl text-[var(--blue-ink)]">Send us a message</h2>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input name="name" required value={form.name} onChange={onChange} className="inputf" data-testid="contact-name" />
                </Field>
                <Field label="Email" required>
                  <input name="email" type="email" required value={form.email} onChange={onChange} className="inputf" data-testid="contact-email" />
                </Field>
                <Field label="Phone (optional)">
                  <input name="phone" value={form.phone} onChange={onChange} className="inputf" data-testid="contact-phone" />
                </Field>
                <Field label="Subject">
                  <select name="subject" value={form.subject} onChange={onChange} className="inputf bg-white" data-testid="contact-subject">
                    <option>General Inquiry</option>
                    <option>Membership</option>
                    <option>Lifecycle Event</option>
                    <option>Religious School</option>
                    <option>Speak with Rabbi</option>
                    <option>Donation Question</option>
                  </select>
                </Field>
              </div>
              <Field label="Message" required>
                <textarea name="message" required rows={6} value={form.message} onChange={onChange} className="inputf" data-testid="contact-message" />
              </Field>
              <button type="submit" disabled={busy} className="btn-primary justify-center w-full md:w-auto disabled:opacity-60" data-testid="contact-submit">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {busy ? "Sending…" : "Send Message"}
              </button>
              <style>{`
                .inputf { width: 100%; padding: 0.85rem 1rem; border: 1px solid var(--line); border-radius: 0.75rem; outline: none; transition: border-color 200ms; background: white; }
                .inputf:focus { border-color: var(--blue); }
              `}</style>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-10 mt-12">
          <iframe
            title="Temple Israel of Brevard location"
            src="https://www.google.com/maps?q=7300+Lake+Andrew+Drive,+Melbourne,+FL+32940&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, borderRadius: "1.5rem" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map"
          />
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[var(--ink)] mb-2">
        {label} {required && <span className="text-[var(--blue-deep)]">*</span>}
      </span>
      {children}
    </label>
  );
}

function InfoCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="card-soft p-6 flex gap-4 bg-white" data-testid={testid}>
      <div className="w-12 h-12 shrink-0 rounded-2xl bg-[var(--blue)]/10 text-[var(--blue-deep)] flex items-center justify-center">
        <Icon size={20} />
      </div>
      <div className="text-[var(--ink)]">
        <p className="eyebrow mb-1">★ {title}</p>
        <div className="leading-relaxed text-[var(--ink-soft)]">{children}</div>
      </div>
    </div>
  );
}
